<app-page-breadcrumb [pageTitle]="isEditing ? 'Editar empresa' : 'Nueva empresa'"></app-page-breadcrumb>

<div class="grid grid-cols-1 gap-6">
  <app-component-card
    [title]="isEditing ? 'Editar empresa' : 'Nueva empresa'"
    desc="Complete los datos de la empresa">

    @if (loading) {
      <div class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
        <span class="ml-3 text-gray-500 dark:text-gray-400">Cargando datos...</span>
      </div>
    }

    @if (error) {
      <div class="mb-4 p-4 bg-error-50 border border-error-200 rounded-lg dark:bg-error-500/10 dark:border-error-500/20">
        <div class="flex items-center gap-3">
          <svg class="w-5 h-5 text-error-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p class="text-error-600 dark:text-error-400">{{ error }}</p>
        </div>
      </div>
    }

    @if (!loading) {
      <form [formGroup]="form" (ngSubmit)="guardar()" class="space-y-5">
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Nombre <span class="text-error-500">*</span>
          </label>
          <input
            type="text"
            formControlName="name"
            placeholder="Nombre de la empresa"
            class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
            [class.border-error-500]="hasError('name')"
            [class.border-gray-300]="!hasError('name')"
          />
          @if (hasError('name')) {
            <p class="mt-1.5 text-sm text-error-500">{{ getError('name') }}</p>
          }
        </div>

        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Descripción
          </label>
          <textarea
            rows="3"
            formControlName="description"
            placeholder="Descripción..."
            maxlength="500"
            class="w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
            [class.border-error-500]="hasError('description')"
            [class.border-gray-300]="!hasError('description')"></textarea>
          <div class="mt-1.5 flex items-center justify-between">
            @if (hasError('description')) {
              <p class="text-sm text-error-500">{{ getError('description') }}</p>
            } @else {
              <span></span>
            }
            <span class="text-xs text-gray-500 dark:text-gray-400">
              {{ (form.get('description')?.value || '').length }}/500
            </span>
          </div>
        </div>

        <!-- Logo upload -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Logotipo
          </label>

          @if (logoUrl) {
            <div class="mb-3 flex items-center gap-4">
              <img [src]="logoUrl" alt="Logo"
                class="h-16 w-auto max-w-[200px] object-contain rounded-lg border border-gray-200 dark:border-gray-700 bg-white p-2" />
              <button type="button" (click)="removeLogo()"
                class="text-sm text-error-500 hover:text-error-600 font-medium transition-colors">
                Eliminar logo
              </button>
            </div>
          }

          <label class="inline-flex cursor-pointer">
            <span class="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm text-gray-700 dark:text-gray-300">
              @if (logoUploading) {
                <span class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"></span>
                Subiendo...
              } @else {
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                </svg>
                {{ logoUrl ? 'Cambiar logo' : 'Subir logo' }}
              }
            </span>
            <input type="file" accept="image/*" class="hidden"
              (change)="onLogoSelected($event)" [disabled]="logoUploading" />
          </label>
          <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
            PNG, JPG o SVG · Aparece en los formularios públicos del business
          </p>
        </div>

        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Tipo de Identificación
            </label>
            <select
              formControlName="identificationType"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800">
              <option value="">-- Elegir tipo --</option>
              @for (tipo of identificationTypes; track tipo) {
                <option [value]="tipo">{{ tipo }}</option>
              }
            </select>
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Número de Identificación <span class="text-error-500">*</span>
            </label>
            <input
              type="text"
              formControlName="identificationNumber"
              placeholder="Ej: 20-1234567"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
              [class.border-error-500]="hasError('identificationNumber')"
              [class.border-gray-300]="!hasError('identificationNumber')"
            />
            @if (hasError('identificationNumber')) {
              <p class="mt-1.5 text-sm text-error-500">{{ getError('identificationNumber') }}</p>
            }
          </div>
        </div>

        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Teléfono
            </label>
            <input
              type="text"
              formControlName="phone"
              inputmode="numeric"
              maxlength="9"
              placeholder="987654321"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
              [class.border-error-500]="hasError('phone')"
              [class.border-gray-300]="!hasError('phone')"
            />
            @if (hasError('phone')) {
              <p class="mt-1.5 text-sm text-error-500">{{ getError('phone') }}</p>
            }
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Dirección
            </label>
            <input
              type="text"
              formControlName="address"
              placeholder="Dirección..."
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
            />
          </div>
        </div>

        <!-- Estado del negocio (activar/desactivar) -->
        <div class="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div class="flex items-start justify-between gap-4">
            <div class="min-w-0">
              <h3 class="text-sm font-semibold text-gray-800 dark:text-white/90">
                Estado
              </h3>
              <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Un business inactivo no puede ser accedido ni operado por sus usuarios.
              </p>
              @if (esProveedora) {
                <p class="mt-2 text-sm font-medium text-brand-600 dark:text-brand-400">
                  Empresa proveedora: siempre activa. No se puede desactivar ni eliminar porque
                  es la que garantiza el acceso del Super Admin al sistema.
                </p>
              }
            </div>

            <label class="relative inline-flex shrink-0 items-center select-none"
                   [class.cursor-pointer]="!esProveedora"
                   [class.cursor-not-allowed]="esProveedora"
                   [class.opacity-60]="esProveedora">
              <input type="checkbox" formControlName="isActive" class="peer sr-only" />
              <div class="h-6 w-11 rounded-full bg-gray-300 transition-colors peer-checked:bg-brand-500 peer-focus:ring-3 peer-focus:ring-brand-500/20 dark:bg-gray-700 after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow-sm after:transition-all peer-checked:after:translate-x-5"></div>
            </label>
          </div>

          <p class="mt-3 text-sm font-medium"
             [class.text-success-600]="form.get('isActive')?.value"
             [class.text-gray-500]="!form.get('isActive')?.value"
             [class.dark:text-success-400]="form.get('isActive')?.value"
             [class.dark:text-gray-400]="!form.get('isActive')?.value">
            {{ form.get('isActive')?.value ? 'Business activo' : 'Business inactivo' }}
          </p>
        </div>

        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Zona horaria predeterminada
          </label>
          <select
            formControlName="defaultTimezone"
            class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800">
            <option value="">Sin zona horaria (usa la del navegador)</option>
            @for (tz of allTimezones; track tz) {
              <option [value]="tz">{{ tz }}</option>
            }
          </select>
          <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
            Los usuarios de este business usarán esta zona horaria por defecto
          </p>
        </div>

        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Moneda por defecto
          </label>
          <select
            formControlName="defaultCurrencyId"
            class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800">
            <option [ngValue]="null">— Sin moneda —</option>
            @for (currency of currencies; track currency.id) {
              <option [ngValue]="currency.id">{{ currency.code }} — {{ currency.name }}</option>
            }
          </select>
          <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
            Se usará como moneda predeterminada al crear cotizaciones de este business
          </p>
        </div>

        <div class="pt-5 border-t border-gray-200 dark:border-gray-700">
          <div class="mb-4">
            <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">
              Administrador de la empresa
            </h3>

            @if (!isEditing) {
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Enviaremos una <b>invitación</b> al correo indicado para que el administrador
                configure su propia contraseña y acceda con rol <b>Admin</b>.
              </p>
            } @else if (adminExists) {
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Actualiza los datos del admin vinculado. La contraseña la gestiona el propio usuario.
              </p>
            } @else {
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Esta empresa todavía no tiene un administrador registrado. Aquí puedes consultar
                el estado de su invitación y reenviarla si hace falta.
              </p>
            }
          </div>

          <!-- INC 15: aviso ámbar cuando el alta creó la empresa pero el correo NO salió -->
          @if (invitationWarning) {
            <div class="mb-5 rounded-lg border border-warning-300 bg-warning-50 p-4 dark:border-warning-500/30 dark:bg-warning-500/10">
              <div class="flex items-start gap-3">
                <svg class="mt-0.5 h-5 w-5 shrink-0 text-warning-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
                <div class="min-w-0">
                  <p class="text-sm font-medium text-warning-700 dark:text-warning-400">Invitación no enviada</p>
                  <p class="mt-1 text-sm text-warning-700 dark:text-warning-400">{{ invitationWarning }}</p>
                </div>
              </div>
            </div>
          }

          <!-- INC 15: estado de la invitación (solo edición). NUNCA se crea una nueva desde aquí. -->
          @if (isEditing) {
            @if (adminInvitationLoading) {
              <div class="mb-5 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <span class="h-4 w-4 animate-spin rounded-full border-b-2 border-brand-500"></span>
                Consultando la invitación del administrador...
              </div>
            } @else if (adminInvitation) {
              <div class="mb-5 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                <div class="flex flex-wrap items-start justify-between gap-3">
                  <div class="min-w-0">
                    <p class="text-sm font-medium text-gray-800 dark:text-white/90">
                      {{ adminInvitationLabel }}
                    </p>
                    @if (adminInvitation.expiresAt && canResendInvitation) {
                      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Vence el {{ formatFecha(adminInvitation.expiresAt) }}
                      </p>
                    }
                  </div>

                  @if (canResendInvitation) {
                    <button type="button" (click)="reenviarInvitacion()" [disabled]="resendingInvitation"
                      class="inline-flex items-center gap-2 rounded-lg border border-brand-500 px-3 py-1.5 text-sm font-medium text-brand-600 transition-colors hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-60 dark:text-brand-400 dark:hover:bg-brand-500/10">
                      @if (resendingInvitation) {
                        <span class="h-4 w-4 animate-spin rounded-full border-b-2 border-brand-500"></span>
                        Reenviando...
                      } @else {
                        Reenviar invitación
                      }
                    </button>
                  }
                </div>
              </div>
            } @else if (!adminExists) {
              <p class="mb-5 text-sm text-gray-500 dark:text-gray-400">
                Esta empresa no tiene una invitación de administrador registrada.
              </p>
            }
          }

          <!-- Nombre/apellido solo al editar un admin YA existente (el invitado los define él mismo) -->
          @if (isEditing && adminExists) {
            <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Nombres <span class="text-error-500">*</span>
                </label>
                <input
                  type="text"
                  formControlName="adminFullname"
                  placeholder="Ej: Juan"
                  class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                  [class.border-error-500]="hasError('adminFullname')"
                  [class.border-gray-300]="!hasError('adminFullname')"
                />
                @if (hasError('adminFullname')) {
                  <p class="mt-1.5 text-sm text-error-500">{{ getError('adminFullname') }}</p>
                }
              </div>

              <div>
                <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Apellidos <span class="text-error-500">*</span>
                </label>
                <input
                  type="text"
                  formControlName="adminLastname"
                  placeholder="Ej: Pérez"
                  class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                  [class.border-error-500]="hasError('adminLastname')"
                  [class.border-gray-300]="!hasError('adminLastname')"
                />
                @if (hasError('adminLastname')) {
                  <p class="mt-1.5 text-sm text-error-500">{{ getError('adminLastname') }}</p>
                }
              </div>
            </div>
          }

          <!-- El correo solo es editable al CREAR (se invita) o al editar un admin YA vinculado.
               Al editar sin admin vinculado el correo lo fija la invitación (solo lectura). -->
          @if (!isEditing || adminExists) {
          <div class="mt-5">
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Correo del administrador <span class="text-error-500">*</span>
            </label>
            <input
              type="email"
              formControlName="adminMail"
              placeholder="admin@empresa.com"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
              [class.border-error-500]="hasError('adminMail')"
              [class.border-gray-300]="!hasError('adminMail')"
            />
            @if (hasError('adminMail')) {
              <p class="mt-1.5 text-sm text-error-500">{{ getError('adminMail') }}</p>
            }
          </div>
          }
        </div>

        <div class="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <app-button variant="outline" type="button" (btnClick)="cancelar()">
            Cancelar
          </app-button>

          <app-button type="button" [disabled]="saving" (btnClick)="guardar()">
            @if (saving) {
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            }

            @if (saving && savingStep === 'business') {
              {{ isEditing ? 'Actualizando empresa...' : 'Creando empresa e invitando al admin...' }}
            } @else if (saving && savingStep === 'admin') {
              Actualizando admin...
            } @else {
              {{ isEditing ? 'Actualizar' : 'Guardar' }}
            }
          </app-button>
        </div>
      </form>
    }
  </app-component-card>
</div>